import { carregarElevacoesCopernicus, ATRIBUICAO_COPERNICUS } from './copernicusTerrain';

export const FONTES_TERRENO = {
  copernicus: 'Copernicus DEM GLO-30',
  terrarium: 'AWS Terrain Tiles (formato Terrarium)',
};
export const FONTE_TERRENO_PADRAO = 'copernicus';

const TERRARIUM_URL = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium';
const TILE_SIZE = 256;
const MAX_TILES = 9;
const cacheTerreno = new Map();
const INTERVALOS_CURVAS = [5, 10, 20, 25, 50];
const CORES_DECLIVIDADE = [
  [60, 157, 131],
  [126, 183, 127],
  [208, 185, 93],
  [223, 139, 69],
  [201, 87, 69],
  [141, 61, 85],
];

function limitarLatitude(lat) {
  return Math.max(-85.05112878, Math.min(85.05112878, lat));
}

function lngLatParaPixel(lng, lat, zoom) {
  const escala = TILE_SIZE * (2 ** zoom);
  const latitude = limitarLatitude(lat) * Math.PI / 180;
  return {
    x: ((lng + 180) / 360) * escala,
    y: (0.5 - Math.log((1 + Math.sin(latitude)) / (1 - Math.sin(latitude))) / (4 * Math.PI)) * escala,
  };
}

function pixelParaLngLat(x, y, zoom) {
  const escala = TILE_SIZE * (2 ** zoom);
  const lng = (x / escala) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / escala;
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(n));
  return { lng, lat };
}

function bboxDoAnel(anel) {
  const lngs = anel.map(([lng]) => lng);
  const lats = anel.map(([, lat]) => lat);
  return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
}

function pontoDentroPoligono(lng, lat, anel) {
  let dentro = false;
  for (let i = 0, j = anel.length - 1; i < anel.length; j = i++) {
    const [xi, yi] = anel[i];
    const [xj, yj] = anel[j];
    const cruza = (yi > lat) !== (yj > lat)
      && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (cruza) dentro = !dentro;
  }
  return dentro;
}

function selecionarZoom(bbox) {
  const [oeste, sul, leste, norte] = bbox;
  for (let zoom = 13; zoom >= 8; zoom--) {
    const inicio = lngLatParaPixel(oeste, norte, zoom);
    const fim = lngLatParaPixel(leste, sul, zoom);
    const colunas = Math.floor(fim.x / TILE_SIZE) - Math.floor(inicio.x / TILE_SIZE) + 1;
    const linhas = Math.floor(fim.y / TILE_SIZE) - Math.floor(inicio.y / TILE_SIZE) + 1;
    if (colunas * linhas <= MAX_TILES) return zoom;
  }
  return 8;
}

async function carregarTile(zoom, x, y) {
  const resposta = await fetch(`${TERRARIUM_URL}/${zoom}/${x}/${y}.png`);
  if (!resposta.ok) throw new Error(`MDE indisponível (HTTP ${resposta.status})`);
  const bitmap = await createImageBitmap(await resposta.blob());
  const canvas = document.createElement('canvas');
  canvas.width = TILE_SIZE;
  canvas.height = TILE_SIZE;
  const contexto = canvas.getContext('2d', { willReadFrequently: true });
  contexto.drawImage(bitmap, 0, 0);
  bitmap.close?.();
  return contexto.getImageData(0, 0, TILE_SIZE, TILE_SIZE).data;
}

function decodificarElevacao(pixels, indice) {
  const offset = indice * 4;
  return pixels[offset] * 256 + pixels[offset + 1] + pixels[offset + 2] / 256 - 32768;
}

function corHipsometrica(normalizado) {
  const paradas = [
    [0, [28, 111, 126]],
    [0.25, [70, 148, 118]],
    [0.5, [163, 181, 107]],
    [0.75, [202, 151, 92]],
    [1, [239, 229, 204]],
  ];
  const valor = Math.max(0, Math.min(1, normalizado));
  const superior = paradas.findIndex(([posicao]) => posicao >= valor);
  if (superior <= 0) return paradas[0][1];
  const [posicaoA, corA] = paradas[superior - 1];
  const [posicaoB, corB] = paradas[superior];
  const proporcao = (valor - posicaoA) / (posicaoB - posicaoA);
  return corA.map((canal, indice) => Math.round(canal + (corB[indice] - canal) * proporcao));
}

function intervaloCurvas(amplitude) {
  if (amplitude <= 25) return 5;
  if (amplitude <= 80) return 10;
  if (amplitude <= 200) return 20;
  return 50;
}

function interpolarPonto(nivel, valorA, valorB, pontoA, pontoB) {
  const diferenca = valorB - valorA;
  const proporcao = Math.abs(diferenca) < 0.0001 ? 0.5 : (nivel - valorA) / diferenca;
  return {
    x: pontoA.x + (pontoB.x - pontoA.x) * proporcao,
    y: pontoA.y + (pontoB.y - pontoA.y) * proporcao,
  };
}

function gerarCurvas(elevacoes, mascara, largura, altura, origemX, origemY, zoom, minimo, maximo, intervalo, passo) {
  const curvas = [];
  const primeiroNivel = Math.ceil(minimo / intervalo) * intervalo;

  for (let nivel = primeiroNivel; nivel <= maximo; nivel += intervalo) {
    for (let y = 0; y < altura - passo; y += passo) {
      for (let x = 0; x < largura - passo; x += passo) {
        const indices = [
          y * largura + x,
          y * largura + x + passo,
          (y + passo) * largura + x + passo,
          (y + passo) * largura + x,
        ];
        if (!indices.every(indice => mascara[indice])) continue;
        const valores = indices.map(indice => elevacoes[indice]);
        const pontos = [
          { x, y }, { x: x + passo, y },
          { x: x + passo, y: y + passo }, { x, y: y + passo },
        ];
        const cruzamentos = [];
        [[0, 1], [1, 2], [2, 3], [3, 0]].forEach(([a, b]) => {
          if ((valores[a] < nivel && valores[b] >= nivel) || (valores[b] < nivel && valores[a] >= nivel)) {
            cruzamentos.push(interpolarPonto(nivel, valores[a], valores[b], pontos[a], pontos[b]));
          }
        });
        if (cruzamentos.length < 2) continue;
        for (let i = 0; i + 1 < cruzamentos.length; i += 2) {
          const inicio = pixelParaLngLat(origemX + cruzamentos[i].x + 0.5, origemY + cruzamentos[i].y + 0.5, zoom);
          const fim = pixelParaLngLat(origemX + cruzamentos[i + 1].x + 0.5, origemY + cruzamentos[i + 1].y + 0.5, zoom);
          curvas.push({ nivel, positions: [[inicio.lat, inicio.lng], [fim.lat, fim.lng]] });
        }
      }
    }
  }
  return curvas;
}

export async function analisarTerreno(poligono, fonte = FONTE_TERRENO_PADRAO) {
  if (!Object.hasOwn(FONTES_TERRENO, fonte)) throw new Error('Fonte de elevação inválida.');
  const geometria = poligono?.type === 'Feature' ? poligono.geometry : poligono;
  const anel = geometria?.coordinates?.[0];
  if (!anel || anel.length < 4) throw new Error('Defina um polígono válido para analisar o terreno.');

  const cacheKey = JSON.stringify([fonte, anel]);
  if (cacheTerreno.has(cacheKey)) return cacheTerreno.get(cacheKey);

  const bbox = bboxDoAnel(anel);
  const zoom = selecionarZoom(bbox);
  const [oeste, sul, leste, norte] = bbox;
  const inicio = lngLatParaPixel(oeste, norte, zoom);
  const fim = lngLatParaPixel(leste, sul, zoom);
  const tileXMin = Math.floor(inicio.x / TILE_SIZE);
  const tileYMin = Math.floor(inicio.y / TILE_SIZE);
  const tileXMax = Math.floor(fim.x / TILE_SIZE);
  const tileYMax = Math.floor(fim.y / TILE_SIZE);
  const largura = (tileXMax - tileXMin + 1) * TILE_SIZE;
  const altura = (tileYMax - tileYMin + 1) * TILE_SIZE;
  const origemX = tileXMin * TILE_SIZE;
  const origemY = tileYMin * TILE_SIZE;
  const elevacoes = fonte === 'copernicus'
    ? await carregarElevacoesCopernicus(largura, altura, (x, y) => pixelParaLngLat(origemX + x + 0.5, origemY + y + 0.5, zoom))
    : new Float32Array(largura * altura);

  if (fonte === 'terrarium') {
    const tiles = [];
    for (let tileY = tileYMin; tileY <= tileYMax; tileY++) {
      for (let tileX = tileXMin; tileX <= tileXMax; tileX++) tiles.push({ tileX, tileY });
    }
    const dadosTiles = await Promise.all(tiles.map(async tile => ({
      ...tile,
      pixels: await carregarTile(zoom, tile.tileX, tile.tileY),
    })));

    dadosTiles.forEach(({ tileX, tileY, pixels }) => {
      const deslocamentoX = (tileX - tileXMin) * TILE_SIZE;
      const deslocamentoY = (tileY - tileYMin) * TILE_SIZE;
      for (let y = 0; y < TILE_SIZE; y++) {
        for (let x = 0; x < TILE_SIZE; x++) {
          elevacoes[(deslocamentoY + y) * largura + deslocamentoX + x] = decodificarElevacao(pixels, y * TILE_SIZE + x);
        }
      }
    });
  }

  const mascara = new Uint8Array(largura * altura);
  let minimo = Infinity;
  let maximo = -Infinity;
  let soma = 0;
  let quantidade = 0;
  for (let y = 0; y < altura; y++) {
    for (let x = 0; x < largura; x++) {
      const indice = y * largura + x;
      const coordenada = pixelParaLngLat(origemX + x + 0.5, origemY + y + 0.5, zoom);
      if (!pontoDentroPoligono(coordenada.lng, coordenada.lat, anel)) continue;
      const elevacao = elevacoes[indice];
      if (!Number.isFinite(elevacao)) continue;
      mascara[indice] = 1;
      minimo = Math.min(minimo, elevacao);
      maximo = Math.max(maximo, elevacao);
      soma += elevacao;
      quantidade++;
    }
  }
  if (!quantidade) throw new Error('O MDE não possui pixels válidos dentro deste talhão.');

  const latitudeMedia = (sul + norte) / 2;
  const metrosPorPixel = 156543.03392 * Math.cos(latitudeMedia * Math.PI / 180) / (2 ** zoom);
  const classesDeclividade = [0, 0, 0, 0, 0, 0];
  const classesPorPixel = new Int8Array(largura * altura).fill(-1);
  let somaDeclividade = 0;
  let maxDeclividade = 0;
  let qtdDeclividade = 0;
  for (let y = 1; y < altura - 1; y++) {
    for (let x = 1; x < largura - 1; x++) {
      const indice = y * largura + x;
      const vizinhos = [indice - largura - 1, indice - largura, indice - largura + 1, indice - 1, indice + 1, indice + largura - 1, indice + largura, indice + largura + 1];
      if (!mascara[indice] || !vizinhos.every(vizinho => mascara[vizinho])) continue;
      const dzdx = ((elevacoes[indice - largura + 1] + 2 * elevacoes[indice + 1] + elevacoes[indice + largura + 1])
        - (elevacoes[indice - largura - 1] + 2 * elevacoes[indice - 1] + elevacoes[indice + largura - 1])) / (8 * metrosPorPixel);
      const dzdy = ((elevacoes[indice + largura - 1] + 2 * elevacoes[indice + largura] + elevacoes[indice + largura + 1])
        - (elevacoes[indice - largura - 1] + 2 * elevacoes[indice - largura] + elevacoes[indice - largura + 1])) / (8 * metrosPorPixel);
      const declividade = Math.hypot(dzdx, dzdy) * 100;
      somaDeclividade += declividade;
      maxDeclividade = Math.max(maxDeclividade, declividade);
      qtdDeclividade++;
      const classe = declividade < 3 ? 0 : declividade < 8 ? 1 : declividade < 13 ? 2 : declividade < 20 ? 3 : declividade < 45 ? 4 : 5;
      classesDeclividade[classe]++;
      classesPorPixel[indice] = classe;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = largura;
  canvas.height = altura;
  const contexto = canvas.getContext('2d');
  const imagem = contexto.createImageData(largura, altura);
  const amplitude = maximo - minimo;
  for (let indice = 0; indice < elevacoes.length; indice++) {
    if (!mascara[indice]) continue;
    const cor = corHipsometrica(amplitude > 0 ? (elevacoes[indice] - minimo) / amplitude : 0.5);
    const offset = indice * 4;
    imagem.data[offset] = cor[0];
    imagem.data[offset + 1] = cor[1];
    imagem.data[offset + 2] = cor[2];
    imagem.data[offset + 3] = 205;
  }
  contexto.putImageData(imagem, 0, 0);

  const canvasDeclividade = document.createElement('canvas');
  canvasDeclividade.width = largura;
  canvasDeclividade.height = altura;
  const contextoDeclividade = canvasDeclividade.getContext('2d');
  const imagemDeclividade = contextoDeclividade.createImageData(largura, altura);
  for (let indice = 0; indice < classesPorPixel.length; indice++) {
    const classe = classesPorPixel[indice];
    if (classe < 0) continue;
    const cor = CORES_DECLIVIDADE[classe];
    const offset = indice * 4;
    imagemDeclividade.data[offset] = cor[0];
    imagemDeclividade.data[offset + 1] = cor[1];
    imagemDeclividade.data[offset + 2] = cor[2];
    imagemDeclividade.data[offset + 3] = 215;
  }
  contextoDeclividade.putImageData(imagemDeclividade, 0, 0);

  const intervalo = intervaloCurvas(amplitude);
  const passoCurvas = fonte === 'copernicus' ? 1 : Math.max(1, Math.ceil(Math.max(largura, altura) / 150));
  const curvasPorIntervalo = Object.fromEntries(INTERVALOS_CURVAS.map(valor => [
    valor,
    gerarCurvas(elevacoes, mascara, largura, altura, origemX, origemY, zoom, minimo, maximo, valor, passoCurvas),
  ]));
  const totalDeclividade = classesDeclividade.reduce((total, valor) => total + valor, 0) || 1;
  const limiteNoroeste = pixelParaLngLat(origemX, origemY, zoom);
  const limiteSudeste = pixelParaLngLat(origemX + largura, origemY + altura, zoom);
  const resultado = {
    altitude: { min: minimo, mean: soma / quantidade, max: maximo, amplitude },
    declividade: {
      mean: qtdDeclividade ? somaDeclividade / qtdDeclividade : 0,
      max: maxDeclividade,
      classes: classesDeclividade.map(valor => (valor / totalDeclividade) * 100),
    },
    intervaloCurvas: intervalo,
    intervalosCurvas: INTERVALOS_CURVAS,
    curvasPorIntervalo,
    mapaAltitude: {
      dataUrl: canvas.toDataURL('image/png'),
      bounds: [[limiteSudeste.lat, limiteNoroeste.lng], [limiteNoroeste.lat, limiteSudeste.lng]],
    },
    mapaDeclividade: {
      dataUrl: canvasDeclividade.toDataURL('image/png'),
      bounds: [[limiteSudeste.lat, limiteNoroeste.lng], [limiteNoroeste.lat, limiteSudeste.lng]],
    },
    resolucao: Math.round(metrosPorPixel),
    fonteId: fonte,
    fonte: FONTES_TERRENO[fonte],
    resolucaoNominal: fonte === 'copernicus' ? 30 : null,
    atribuicao: fonte === 'copernicus' ? ATRIBUICAO_COPERNICUS : null,
  };
  cacheTerreno.set(cacheKey, resultado);
  return resultado;
}
